import React, { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, X, Loader2, Save, Search, Wrench } from "lucide-react";
import api from "../../lib/api";
import ImageUpload from "../../components/admin/ImageUpload";
import Pagination from "../../components/admin/Pagination";

const emptyForm = {
  type: "rental", slug: "",
  name: { en: "", ar: "" },
  short: { en: "", ar: "" },
  description: { en: "", ar: "" },
  bullets: { en: [], ar: [] },
  capacity: "", image: "", order: 0, is_active: true,
};

const AdminServices = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeLang, setActiveLang] = useState("en");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(6);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/services");
      setItems(res.data);
    } catch { toast.error("Failed to load services"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId("new"); setForm({ ...emptyForm }); setActiveLang("en"); };
  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      type: item.type, slug: item.slug,
      name: { en: item.name?.en || "", ar: item.name?.ar || "" },
      short: { en: item.short?.en || "", ar: item.short?.ar || "" },
      description: { en: item.description?.en || "", ar: item.description?.ar || "" },
      bullets: { en: item.bullets?.en || [], ar: item.bullets?.ar || [] },
      capacity: item.capacity || "", image: item.image || "",
      order: item.order || 0, is_active: item.is_active !== false,
    });
    setActiveLang("en");
  };
  const close = () => { setEditingId(null); setForm(emptyForm); };

  const save = async () => {
    if (!form.slug || !form.name.en) { toast.error("Slug and English name are required"); return; }
    setSaving(true);
    try {
      if (editingId === "new") { await api.post("/admin/services", form); toast.success("Service created"); }
      else { await api.put(`/admin/services/${editingId}`, form); toast.success("Service updated"); }
      await load(); close();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await api.delete(`/admin/services/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const filtered = useMemo(() => {
    let list = items;
    if (filter !== "all") list = list.filter((i) => i.type === filter);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter((i) => [i.name?.en, i.name?.ar, i.slug].some((v) => (v || "").toLowerCase().includes(q)));
    }
    return list;
  }, [items, filter, query]);

  // Reset page on filter / search change
  useEffect(() => { setPage(1); }, [filter, query, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const counts = {
    all: items.length,
    rental: items.filter((i) => i.type === "rental").length,
    other: items.filter((i) => i.type === "other").length,
  };

  const updateBullets = (lang, text) => {
    const arr = text.split("\n").map((l) => l.trim()).filter(Boolean);
    setForm((f) => ({ ...f, bullets: { ...f.bullets, [lang]: arr } }));
  };

  return (
    <div data-testid="admin-services-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Services & Equipment</h1>
          <p className="mt-1 text-sm text-slate-500">Manage your rental fleet and contracting services.</p>
        </div>
        <button onClick={openNew} className="admin-btn-primary" data-testid="services-add-btn">
          <Plus size={16} /> New Item
        </button>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
        <div className="flex gap-1.5 bg-slate-100 p-1 rounded-lg w-full md:w-fit overflow-x-auto admin-scroll">
          {[
            { k: "all", l: "All" },
            { k: "rental", l: "Rental" },
            { k: "other", l: "Other" },
          ].map((s) => (
            <button
              key={s.k}
              onClick={() => setFilter(s.k)}
              className={`px-3.5 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-colors ${
                filter === s.k ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
              }`}
              data-testid={`svc-filter-${s.k}`}
            >
              {s.l} <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[10px] ${filter === s.k ? "bg-orange-100 text-orange-700" : "bg-slate-200 text-slate-600"}`}>{counts[s.k]}</span>
            </button>
          ))}
        </div>
        <div className="relative flex-1 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search services..." className="admin-input pl-9" />
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="skeleton h-72 rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <div className="inline-flex w-14 h-14 bg-slate-100 text-slate-400 rounded-full items-center justify-center mb-4"><Wrench size={22} /></div>
          <h3 className="font-semibold text-slate-900">No services yet</h3>
          <p className="mt-1 text-sm text-slate-500">Click "New Item" to add a rental machine or contracting service.</p>
        </div>
      ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paged.map((item) => (
            <div key={item.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col admin-card-hover" data-testid={`svc-card-${item.slug}`}>
              <div className="aspect-[16/9] bg-slate-100 relative">
                {item.image && <img src={item.image} alt={item.name?.en} className="w-full h-full object-cover" />}
                <div className="absolute top-3 left-3 flex gap-1.5">
                  <span className={`admin-badge ${item.type === "rental" ? "admin-badge-rental" : "admin-badge-other"}`}>{item.type}</span>
                  {!item.is_active && <span className="admin-badge admin-badge-hidden">Hidden</span>}
                </div>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-semibold text-slate-900">{item.name?.en}</h3>
                {item.name?.ar && <p className="text-xs text-slate-500 mt-0.5" dir="rtl">{item.name.ar}</p>}
                {item.capacity && <p className="text-xs text-slate-500 mt-1.5 font-mono">{item.capacity}</p>}
                <div className="mt-auto pt-4 flex gap-2">
                  <button onClick={() => openEdit(item)} className="admin-btn-secondary flex-1 text-xs" data-testid={`svc-edit-${item.slug}`}><Edit2 size={12} /> Edit</button>
                  <button onClick={() => remove(item.id)} className="admin-btn-danger" data-testid={`svc-delete-${item.slug}`}><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={filtered.length}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[6, 9, 12, 18]}
        />
        </>
      )}

      {editingId && (
        <div className="fixed inset-0 z-50 admin-modal-backdrop flex items-center justify-center p-3 md:p-6" onClick={close}>
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] shadow-2xl admin-fade-up flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3.5 border-b border-slate-200 flex justify-between items-center bg-white rounded-t-2xl shrink-0">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-orange-600">{editingId === "new" ? "New" : "Edit"}</p>
                <h3 className="text-lg font-bold text-slate-900">Service Item</h3>
              </div>
              <button onClick={close} className="admin-btn-ghost p-2" data-testid="svc-modal-close"><X size={16} /></button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto admin-scroll flex-1">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Type</label>
                  <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="admin-input" data-testid="svc-form-type">
                    <option value="rental">Rental Equipment</option>
                    <option value="other">Other Service</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Slug <span className="text-slate-400">*</span></label>
                  <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} placeholder="excavators" className="admin-input font-mono" data-testid="svc-form-slug" />
                </div>
              </div>

              {/* Language tabs */}
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="flex border-b border-slate-200 bg-slate-50">
                  {["en", "ar"].map((lang) => (
                    <button
                      key={lang}
                      onClick={() => setActiveLang(lang)}
                      className={`px-5 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                        activeLang === lang ? "bg-white text-orange-600 border-b-2 border-orange-500" : "text-slate-500 hover:text-slate-900"
                      }`}
                      data-testid={`svc-lang-${lang}`}
                    >
                      {lang === "en" ? "🇬🇧 English" : "🇶🇦 العربية"}
                    </button>
                  ))}
                </div>
                <div className="p-5 space-y-4" dir={activeLang === "ar" ? "rtl" : "ltr"}>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Name {activeLang === "en" && <span className="text-slate-400">*</span>}</label>
                    <input value={form.name[activeLang]} onChange={(e) => setForm({ ...form, name: { ...form.name, [activeLang]: e.target.value } })} className="admin-input" data-testid={`svc-form-name-${activeLang}`} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Short tagline</label>
                    <input value={form.short[activeLang]} onChange={(e) => setForm({ ...form, short: { ...form.short, [activeLang]: e.target.value } })} className="admin-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1.5">Description</label>
                    <textarea rows={4} value={form.description[activeLang]} onChange={(e) => setForm({ ...form, description: { ...form.description, [activeLang]: e.target.value } })} className="admin-input resize-none" />
                  </div>
                  {form.type === "other" && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 mb-1.5">Bullets <span className="text-slate-400 font-normal">— one per line</span></label>
                      <textarea rows={5} value={form.bullets[activeLang].join("\n")} onChange={(e) => updateBullets(activeLang, e.target.value)} className="admin-input resize-none" />
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Capacity</label>
                  <input value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} placeholder="1.5T – 50T" className="admin-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Display Order</label>
                  <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="admin-input" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Visibility</label>
                  <label className="inline-flex items-center gap-2.5 mt-2 cursor-pointer">
                    <span className={`relative w-10 h-5 rounded-full transition-colors ${form.is_active ? "bg-orange-500" : "bg-slate-300"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow-sm ${form.is_active ? "translate-x-5" : ""}`} />
                    </span>
                    <input type="checkbox" hidden checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
                    <span className="text-sm text-slate-700">{form.is_active ? "Active" : "Hidden"}</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Image</label>
                <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} testId="svc-form-image" aspectClass="aspect-video w-full max-w-md" />
              </div>
            </div>

            <div className="px-5 py-3 border-t border-slate-200 flex gap-2 bg-white rounded-b-2xl shrink-0">
              <button onClick={close} className="admin-btn-secondary flex-1">Cancel</button>
              <button onClick={save} disabled={saving} className="admin-btn-primary flex-1" data-testid="svc-form-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServices;
